/**
 * Runs one download and keeps the card busy while it does (checker findings
 * on 98f18747). Pure: the calls, the fetch, the save and the toasts are passed
 * in, so every path is tested under the functions package's vitest
 * (functions/src/downloads/downloadRunnerWeb.test.ts). useDocumentDownload.ts
 * supplies the real ones.
 *
 * The busy store is keyed per PROJECT and card, so a long download on study A
 * never shows as running on study B's card, and always released in `finally`.
 */
import {
  base64ToBytes,
  downloadErrorMessage,
  downloadSuccessMessage,
  normaliseDownloadReply,
} from './downloadReply';
import type { CallableSpec, DownloadRequest } from './studyDocumentDownloads';

export interface BusyEntry {
  id: string;
  docName: string;
  slow: boolean;
  startedAt: number;
}

export interface BusyStore {
  get: (key: string) => BusyEntry | null;
  /** False when the key is already busy; nothing changes then. */
  tryBegin: (key: string, entry: BusyEntry) => boolean;
  end: (key: string) => void;
  subscribe: (listener: () => void) => () => void;
}

export function busyKey(projectId: string | undefined, card: string): string {
  return `${projectId ?? ''}:${card}`;
}

export function createBusyStore(): BusyStore {
  const running = new Map<string, BusyEntry>();
  const listeners = new Set<() => void>();
  const emit = () => {
    for (const l of listeners) l();
  };
  return {
    get: (key) => running.get(key) ?? null,
    tryBegin: (key, entry) => {
      if (running.has(key)) return false;
      running.set(key, entry);
      emit();
      return true;
    },
    end: (key) => {
      if (running.delete(key)) emit();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The page-wide store every Download button shares. */
export const downloadBusyStore = createBusyStore();

export const DEFAULT_FETCH_TIMEOUT_MS = 300_000;

export function busyRefusalMessage(runningDocName: string): string {
  return `${runningDocName} is already being prepared.`;
}

/** Fetches a signed URL, giving up after `timeoutMs` so a hung fetch cannot hold the card. */
export async function fetchWithTimeout(fetchImpl: typeof fetch, url: string, timeoutMs: number): Promise<Blob> {
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, timeoutMs);
  try {
    let resp: Response;
    try {
      resp = await fetchImpl(url, { signal: ctrl.signal });
    } catch (err) {
      if (timedOut) throw new Error('the file took too long to download. Please try again.');
      throw err;
    }
    if (!resp.ok) throw new Error(`the file could not be fetched (${resp.status} ${resp.statusText}).`);
    return await resp.blob();
  } finally {
    clearTimeout(timer);
  }
}

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

export interface RunnerDeps {
  store: BusyStore;
  now: () => number;
  toast: (message: string, type: ToastKind) => void;
  call: (spec: CallableSpec) => Promise<unknown>;
  fetchFile: (url: string, timeoutMs: number) => Promise<Blob>;
  save: (blob: Blob, filename: string) => void;
}

export type RunOutcome = 'done' | 'refused' | 'failed' | 'busy';

export async function runDocumentDownload(key: string, req: DownloadRequest, deps: RunnerDeps): Promise<RunOutcome> {
  // Synchronous: the store is written before any await, so a double click,
  // or the same card from its face and its panel, cannot start a second run.
  const entry: BusyEntry = { id: req.id, docName: req.docName, slow: !!req.slow, startedAt: deps.now() };
  if (!deps.store.tryBegin(key, entry)) {
    const busy = deps.store.get(key);
    deps.toast(busyRefusalMessage(busy?.docName ?? req.docName), 'info');
    return 'busy';
  }
  try {
    req.onStart?.();

    if (req.precheck) {
      const blocked = await req.precheck();
      if (blocked) {
        deps.toast(blocked, 'error');
        return 'refused';
      }
    }

    if (req.build) {
      const built = await req.build();
      if (built !== 'saved') deps.save(built.blob, built.filename);
      deps.toast(downloadSuccessMessage(req.docName), 'success');
      return 'done';
    }

    if (!req.callable) throw new Error('nothing was asked of the server.');
    if (req.prepare) await deps.call(req.prepare);
    const data = await deps.call(req.callable);
    const filenameOverride = req.filename ? await req.filename(data) : undefined;
    const reply = normaliseDownloadReply(data, {
      docName: req.docName,
      fallbackFilename: req.fallbackFilename || 'download',
      ...(filenameOverride ? { filenameOverride } : {}),
      ...(req.refusal ? { refusal: req.refusal } : {}),
    });

    switch (reply.kind) {
      case 'blocked':
        deps.toast(reply.message, 'error');
        return 'refused';
      case 'nothing':
        // The verification warning first, then the notice (old SDTM order).
        if (reply.warning) deps.toast(reply.warning, 'warning');
        // A caller with its own notice (the SDTM panel) shows it there.
        if (req.onNothing) req.onNothing();
        else if (req.nothingMessage) deps.toast(req.nothingMessage, 'info');
        return 'done';
      case 'unrecognised':
        if (!req.onUnrecognised) throw new Error('no file came back from the server.');
        await req.onUnrecognised(data);
        break;
      case 'url': {
        // Signed Storage URL: callable replies are capped near 10MB, which a
        // full SDRG or submission package can exceed.
        const blob = await deps.fetchFile(reply.url, req.callable.timeout ?? DEFAULT_FETCH_TIMEOUT_MS);
        deps.save(reply.mimeType ? new Blob([blob], { type: reply.mimeType }) : blob, reply.filename);
        break;
      }
      case 'base64':
        deps.save(new Blob([base64ToBytes(reply.base64) as BlobPart], { type: reply.mimeType }), reply.filename);
        break;
      case 'text':
        deps.save(new Blob([reply.text], { type: reply.mimeType }), reply.filename);
        break;
    }
    deps.toast(downloadSuccessMessage(req.docName), 'success');
    if (reply.warning) deps.toast(reply.warning, 'warning');
    return 'done';
  } catch (err) {
    console.error(`Download failed (${req.id}):`, err);
    deps.toast(req.describeError?.(err) ?? downloadErrorMessage(req.docName, err), 'error');
    return 'failed';
  } finally {
    deps.store.end(key);
  }
}
